/**
 * @file ProductOrganisationSection.tsx
 *
 * Groups a product into categories and (optionally) links related products.
 * Both use the shared {@link CheckboxListPicker}; related products are only
 * picked when the merchant switches from “Automatic” to “Choose manually”.
 * Rendered as a Gravity {@link Accordion} so each list can be collapsed.
 */

import { useMemo } from 'react';
import { Accordion, Action, Icon, Radio } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ShopCategory, ShopProduct } from '../../../api/shop-types';
import {
  CheckboxListPicker,
  type CheckboxListPickerItem,
} from '../../../components/CheckboxListPicker';
import type { RelatedProductsMode } from '../product-form-types';

interface ProductOrganisationSectionProps {
  categories: ShopCategory[];
  selectedCategoryIds: string[];
  onCategoryIdsChange: (ids: string[]) => void;
  relatedCandidates: ShopProduct[];
  relatedMode: RelatedProductsMode;
  onRelatedModeChange: (mode: RelatedProductsMode) => void;
  selectedRelatedIds: string[];
  onRelatedIdsChange: (ids: string[]) => void;
}

/**
 * Renders the categories and related-products pickers.
 *
 * @param props - Categories, related candidates, current selections, and handlers.
 *
 * @returns The product-organisation section.
 */
export function ProductOrganisationSection({
  categories,
  selectedCategoryIds,
  onCategoryIdsChange,
  relatedCandidates,
  relatedMode,
  onRelatedModeChange,
  selectedRelatedIds,
  onRelatedIdsChange,
}: ProductOrganisationSectionProps) {
  const intl = useIntl();

  const pickerLabels = {
    allLabel: intl.formatMessage(adminMessages.formPickerAll),
    selectedLabel: intl.formatMessage(adminMessages.formPickerSelected),
    unselectedLabel: intl.formatMessage(adminMessages.formPickerUnselected),
    clearLabel: intl.formatMessage(adminMessages.formPickerClear),
    noResultsLabel: intl.formatMessage(adminMessages.formPickerNoResults),
    noResultsDescriptionLabel: intl.formatMessage(
      adminMessages.formPickerNoResultsDescription,
    ),
    expandLabel: intl.formatMessage(adminMessages.formPickerExpand),
    collapseLabel: intl.formatMessage(adminMessages.formPickerCollapse),
  };

  const categoryItems: CheckboxListPickerItem[] = useMemo(
    () =>
      categories.map((c) => ({
        id: c.id,
        name: c.name,
        parent: c.parent,
        image: c.image,
      })),
    [categories],
  );

  const relatedItems: CheckboxListPickerItem[] = useMemo(
    () =>
      relatedCandidates.map((p) => ({
        id: p.id,
        name: p.name,
        parent: '',
        image: p.images[0] ? { src: p.images[0].src } : null,
      })),
    [relatedCandidates],
  );

  const categoriesTitle = selectedCategoryIds.length
    ? intl.formatMessage(adminMessages.formCategoriesWithCount, {
        count: selectedCategoryIds.length,
      })
    : intl.formatMessage(adminMessages.formCategories);

  const relatedTitle = selectedRelatedIds.length
    ? intl.formatMessage(adminMessages.formRelatedProductsWithCount, {
        count: selectedRelatedIds.length,
      })
    : intl.formatMessage(adminMessages.formRelatedProducts);

  const relatedOptions: {
    id: RelatedProductsMode;
    label: string;
    description?: string;
  }[] = [
    {
      id: 'auto',
      label: intl.formatMessage(adminMessages.formRelatedAuto),
      description: intl.formatMessage(adminMessages.formRelatedAutoDescription),
    },
    { id: 'manual', label: intl.formatMessage(adminMessages.formRelatedManual) },
    { id: 'none', label: intl.formatMessage(adminMessages.formRelatedNone) },
  ];

  const items = [
    {
      id: 'categories',
      title: categoriesTitle,
      content: (
        <div className="gv-flex gv-flex-col gv-gap-md gv-w-full gv-pt-sm">
          <p className="gv-text-sm">
            {intl.formatMessage(adminMessages.formCategoryDescription)}
          </p>
          {categoryItems.length > 0 && (
            <CheckboxListPicker
              testId="product-form-categories"
              items={categoryItems}
              selectedIds={selectedCategoryIds}
              onSelectionChange={onCategoryIdsChange}
              {...pickerLabels}
            />
          )}
          <Action
            component="a"
            href="/categories/new"
            target="_blank"
            rel="noopener noreferrer"
            className="gv-flex gv-items-center gv-gap-xs"
            style={{ color: 'var(--color-state-form-active, #0066aa)' }}
          >
            <span className="gv-text-sm gv-text-bold">
              {intl.formatMessage(adminMessages.formCreateCategory)}
            </span>
            <Icon name="open_in_new" size="small" />
          </Action>
        </div>
      ),
    },
    {
      id: 'related',
      title: relatedTitle,
      content: (
        <div className="gv-flex gv-flex-col gv-gap-md gv-w-full gv-pt-sm">
          <p className="gv-text-sm">
            {intl.formatMessage(adminMessages.formRelatedDescription)}
          </p>
          <div className="gv-flex gv-flex-col gv-gap-md gv-w-full">
            {relatedOptions.map((option) => (
              <div
                key={option.id}
                className="gv-flex gv-flex-col gv-gap-sm gv-w-full"
              >
                <label className="gv-flex gv-items-center gv-gap-sm gv-w-full">
                  <Radio
                    condensed
                    groupName="relatedMode"
                    checked={relatedMode === option.id}
                    onChange={() => onRelatedModeChange(option.id)}
                  />
                  <div className="gv-flex gv-flex-col gv-gap-xs">
                    <span className="gv-text-sm">{option.label}</span>
                    {option.description && (
                      <span className="gv-caption-lg gv-text-on-alternative">
                        {option.description}
                      </span>
                    )}
                  </div>
                </label>
                {option.id === 'manual' &&
                  relatedMode === 'manual' &&
                  (relatedItems.length === 0 ? (
                    <div className="gv-border gv-radius gv-surface-dim gv-p-md gv-text-center">
                      {intl.formatMessage(adminMessages.formRelatedNoProducts)}
                    </div>
                  ) : (
                    <div className="gv-pl-lg gv-w-full">
                      <CheckboxListPicker
                        testId="product-form-related"
                        items={relatedItems}
                        selectedIds={selectedRelatedIds}
                        onSelectionChange={onRelatedIdsChange}
                        {...pickerLabels}
                      />
                    </div>
                  ))}
              </div>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return <Accordion items={items} />;
}
